import React, { useState, useMemo, useCallback } from "react";
import { OpportunityTable } from "../opportunities/OpportunityTable";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Plus,
  Calendar,
  Edit,
  AlertCircle,
  Settings,
  Loader2,
  TrendingUp,
  Building2,
  User,
  Tag,
  Trophy,
  Trash2,
} from "lucide-react";
import { parseISO } from "date-fns";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Opportunity,
  OpportunityStage,
  MoveStageData,
} from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";

interface OpportunityKanbanProps {
  slug: string;
  opportunities: Opportunity[];
  stages: OpportunityStage[];
  onOpportunityClick: (opportunity: Opportunity) => void;
  onCreateOpportunity: () => void;
  onStageEdit: () => void;
  onStageChange?: (opportunityId: string, newStageId: string) => void;
  view?: "kanban" | "table";
}

interface ValidationResult {
  allowed: boolean;
  reason?: string;
  opportunity?: Opportunity;
  targetStage?: string;
}

const getStageColor = (stageType: string) => {
  switch (stageType) {
    case "closed_won":
      return "bg-slate-900 hover:bg-black";
    case "closed_lost":
      return "bg-red-500 hover:bg-red-600";
    default:
      return "bg-gray-500 hover:bg-gray-600";
  }
};

const getStageLabel = (stageType: string, stageName: string) => {
  switch (stageType) {
    case "closed_won":
      return "Ganada";
    case "closed_lost":
      return "Perdida";
    default:
      // Para etapas normales (start, progress, custom) no mostramos badge
      return null;
  }
};

const getPriorityConfig = (priority?: string) => {
  switch (priority) {
    case "large":
    case "Grande":
      return { label: "Grande", className: "bg-red-100 text-red-700" };
    case "medium":
    case "Mediana":
      return { label: "Mediana", className: "bg-yellow-100 text-yellow-700" };
    case "small":
    case "Pequeña":
      return { label: "Pequeña", className: "bg-green-100 text-green-700" };
    default:
      return null;
  }
};

const getDateInfo = (dateString: string) => {
  if (!dateString) return null;
  try {
    let date = parseISO(dateString);
    if (isNaN(date.getTime())) {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return null;
    const today = new Date();
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const d2 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffTime = d1.getTime() - d2.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return {
        text: `${Math.abs(diffDays)} días retraso`,
        className: "bg-red-100 text-red-700 text-xs",
        iconClass: "text-red-600",
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

const formatNumberWithCommas = (value: string | number): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "0,00";
  return numValue.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const OpportunityCard = React.memo(
  ({
    opportunity,
    index,
    stages,
    loadingId,
    currencySymbol,
    wonStageId,
    lostStageId,
    onClick,
    onQuickAction,
  }: {
    opportunity: Opportunity;
    index: number;
    stages: OpportunityStage[];
    loadingId: string | null;
    currencySymbol: string;
    wonStageId?: string;
    lostStageId?: string;
    onClick: (opportunity: Opportunity) => void;
    onQuickAction: (opportunityId: string, targetStageId: string) => void;
  }) => {
    const priorityConfig = getPriorityConfig(opportunity.priority);
    const dateInfo = getDateInfo(opportunity.estimated_close_date || "");
    const isLoading = loadingId === opportunity.id;
    const currentStage = stages.find((s) => s.id === opportunity.stage_id);
    const stageLabel = currentStage
      ? getStageLabel(currentStage.stage_type, currentStage.name)
      : null;
    const stageBadgeColor = currentStage
      ? getStageColor(currentStage.stage_type)
      : "bg-gray-500";
    const probability = opportunity.success_probability || 0;
    const progressColor =
      probability >= 75
        ? "bg-green-500"
        : probability >= 50
          ? "bg-yellow-400"
          : probability >= 25
            ? "bg-orange-400"
            : "bg-red-400";
    return (
      <Draggable draggableId={opportunity.id} index={index}>
        {(provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={() => onClick(opportunity)}
            className={`group relative cursor-pointer select-none transition-all duration-200 bg-white border border-slate-100 rounded-[16px] hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 active:scale-[0.98] ${
              snapshot.isDragging
                ? "shadow-xl rotate-2 opacity-95 ring-2 ring-indigo-500/20"
                : "shadow-sm"
            } ${isLoading ? "opacity-60 pointer-events-none" : ""}`}
          >
            <CardContent className="p-4 space-y-3 relative">
              <div className="flex items-start justify-between gap-2">
                <p className="font-bold text-sm text-slate-800 leading-tight line-clamp-2 flex-1">
                  {opportunity.name}
                </p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isLoading && (
                    <Loader2 className="h-3.5 w-3.5 text-indigo-500 animate-spin" />
                  )}
                  {stageLabel && (
                    <span
                      className={`text-[10px] font-bold text-white px-2 py-0.5 rounded-md whitespace-nowrap ${stageBadgeColor}`}
                    >
                      {stageLabel}
                    </span>
                  )}
                </div>
              </div>
              


              {opportunity.client_company && (
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                  <span className="truncate font-medium text-slate-700">
                    {typeof opportunity.client_company === "string"
                      ? opportunity.client_company
                      : (opportunity.client_company as any)?.name || ""}
                  </span>
                </div>
              )}

              {opportunity.estimated_value && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                      {currencySymbol} {formatNumberWithCommas(opportunity.estimated_value)}
                    </span>
                    <span className="text-[10px] font-bold text-slate-500 flex items-center gap-0.5">
                      <TrendingUp className="h-3 w-3" />
                      {probability}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${progressColor}`}
                      style={{ width: `${Math.min(probability, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-50">
                <div className="flex flex-wrap items-center gap-2">
                  {priorityConfig && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${priorityConfig.className}`}>
                      {priorityConfig.label}
                    </span>
                  )}
                  {dateInfo && (
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${dateInfo.className}`}>
                      <Calendar className={`h-3 w-3 ${dateInfo.iconClass}`} />
                      <span>{dateInfo.text}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {(opportunity.version_number !== undefined || opportunity.minor_version !== undefined) && (
                    <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                      v{opportunity.version_number ?? 1}.{opportunity.minor_version ?? 0}
                    </span>
                  )}

                  {(opportunity.contact || opportunity.creator) && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-semibold text-slate-600 max-w-[80px] truncate">
                        {opportunity.contact
                          ? typeof opportunity.contact === "string"
                            ? opportunity.contact
                            : (opportunity.contact as any)?.name ||
                              (opportunity.creator
                                ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                                : "")
                          : opportunity.creator
                            ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                            : ""}
                      </span>
                      <Avatar className="h-6 w-6 ring-2 ring-white shadow-sm">
                        <AvatarFallback className="text-[10px] bg-indigo-100 text-indigo-700 font-bold">
                          {(opportunity.contact
                            ? typeof opportunity.contact === "string"
                              ? opportunity.contact
                              : (opportunity.contact as any)?.name ||
                                (opportunity.creator
                                  ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                                  : "") ||
                                ""
                            : (opportunity.creator
                                ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                                : "") || ""
                          )
                            .split(" ")[0]?.[0]
                            ?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </Draggable>
    );
  },
);

export function OpportunityKanban({
  slug,
  opportunities = [],
  stages = [],
  onOpportunityClick,
  onCreateOpportunity,
  onStageEdit,
  onStageChange,
  view = "kanban",
}: OpportunityKanbanProps) {
  const { toast } = useToast();
  const { company, can } = useAuth();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [validationAlert, setValidationAlert] =
    useState<ValidationResult | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const [optimisticOpportunities, setOptimisticOpportunities] = useState<
    Record<string, Opportunity[]>
  >({});

  const globalCurrencySymbol = useMemo(() => {
    if ((company as any)?.currency) return (company as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  }, [company]);

  const sortedStages = useMemo(
    () =>
      [...stages]
        .filter(
          (s) =>
            s.stage_type !== "closed_won" && s.stage_type !== "closed_lost",
        )
        .sort((a, b) => a.order_position - b.order_position),
    [stages],
  );

  const wonStageId = useMemo(
    () => stages.find((s) => s.stage_type === "closed_won")?.id,
    [stages],
  );

  const lostStageId = useMemo(
    () => stages.find((s) => s.stage_type === "closed_lost")?.id,
    [stages],
  );

  const handleQuickAction = async (opportunityId: string, targetStageId: string) => {
    setLoadingId(opportunityId);
    try {
      const { opportunityAPI } = await import("@/services/opportunityService");
      const moveData: MoveStageData = {
        slug,
        stage_id: targetStageId,
      };
      await opportunityAPI.moveOpportunityStage(opportunityId, moveData);
      onStageChange?.(opportunityId, targetStageId);
      
      const targetStage = stages.find(s => s.id === targetStageId);
      toast({
        title: "Oportunidad actualizada",
        description: `Se movió a la etapa ${targetStage?.name || targetStageId}`,
      });
    } catch (error: any) {
      toast({
        title: "Error al actualizar oportunidad",
        description: error.message || "No se pudo actualizar la oportunidad",
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  const opportunitiesByStage = useMemo(() => {
    if (Object.keys(optimisticOpportunities).length > 0)
      return optimisticOpportunities;
    return sortedStages.reduce(
      (acc, stage) => {
        acc[stage.id] = opportunities.filter(
          (opp) => opp.stage_id === stage.id,
        );
        return acc;
      },
      {} as Record<string, Opportunity[]>,
    );
  }, [sortedStages, opportunities, optimisticOpportunities]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const opportunity = opportunities.find((opp) => opp.id === draggableId);
    if (!opportunity) return;

    const sourceStage = stages.find((s) => s.id === source.droppableId);
    const destinationStage = stages.find(
      (s) => s.id === destination.droppableId,
    );

    const updatedOpportunities = { ...opportunitiesByStage };

    if (updatedOpportunities[source.droppableId]) {
      updatedOpportunities[source.droppableId] = updatedOpportunities[
        source.droppableId
      ].filter((opp) => opp.id !== draggableId);
    }

    const updatedOpportunity = {
      ...opportunity,
      stage_id: destination.droppableId,
    };

    const destinationList = [
      ...(updatedOpportunities[destination.droppableId] || []),
    ];
    destinationList.splice(destination.index, 0, updatedOpportunity);
    updatedOpportunities[destination.droppableId] = destinationList;

    setOptimisticOpportunities(updatedOpportunities);
    setLoadingId(draggableId);

    try {
      const { opportunityAPI } = await import("@/services/opportunityService");

      const moveData: MoveStageData = {
        slug,
        stage_id: destination.droppableId,
      };

      await opportunityAPI.moveOpportunityStage(draggableId, moveData);

      onStageChange?.(draggableId, destination.droppableId);

      toast({
        title: "Oportunidad movida",
        description: "La oportunidad se ha movido exitosamente",
      });
    } catch (error: any) {
      setOptimisticOpportunities({});

      toast({
        title: "Error al mover oportunidad",
        description: error.message || "No se pudo mover la oportunidad",
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  if (!sortedStages || sortedStages.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No hay etapas configuradas
        </h3>
        <p className="text-slate-600 text-base mb-4">
          Configura las etapas del pipeline primero
        </p>
        <Button
          onClick={onStageEdit}
          className="gap-2 bg-slate-900 hover:bg-black text-sm h-10 px-6"
          size="default"
        >
          <Settings className="h-4 w-4" />
          Configurar Etapas
        </Button>
      </div>
    );
  }

  return (
    <>
      {view === "kanban" ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex space-x-6 overflow-x-auto pb-6 custom-scrollbar snap-x snap-mandatory items-start min-h-[500px]">
            {sortedStages.map((stage) => {
              const stageOpportunities = opportunitiesByStage[stage.id] || [];
              const totalValue = stageOpportunities.reduce(
                (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
                0,
              );

              return (
                <Droppable droppableId={stage.id} key={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      className="flex-shrink-0 w-80 snap-center flex flex-col h-full"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      <div
                        className="rounded-t-[20px] p-4 text-white shadow-sm relative z-10"
                        style={{
                          backgroundColor: stage.color || "#4f46e5",
                          backgroundImage: `linear-gradient(135deg, ${
                            stage.color || "#4f46e5"
                          } 0%, ${
                            stage.color ? `${stage.color}CC` : "#4338ca"
                          } 100%)`,
                        }}
                      >
                        <div className="flex items-center justify-between mb-3 pt-1">
                          <div className="flex items-center space-x-2.5">
                            <div className="h-2.5 w-2.5 bg-white/40 rounded-full"></div>
                            <h3 className="font-bold text-white text-sm tracking-tight capitalize truncate max-w-[160px]">
                              {stage.name}
                            </h3>
                          </div>
                          <Badge
                            variant="secondary"
                            className="bg-white/20 text-white font-bold px-2 py-0.5 text-xs rounded-md border-0"
                          >
                            {stageOpportunities.length}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex flex-col">
                            <span className="text-white/80 font-medium text-[10px]">Valor total</span>
                            <span className="font-bold text-white text-sm">
                              {globalCurrencySymbol} {formatNumberWithCommas(totalValue)}
                            </span>
                          </div>
                          {can("stages.manage") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 hover:bg-white/20 rounded-lg text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                onStageEdit();
                              }}
                              title="Editar etapa"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <div
                        className={`
                        max-h-[500px] overflow-y-auto custom-scrollbar p-3 space-y-3 rounded-b-[20px] transition-colors duration-300 border border-slate-200 shadow-sm
                        ${
                          snapshot.isDraggingOver
                            ? "bg-slate-50/80 border-dashed border-2 border-indigo-300"
                            : "bg-slate-50/50"
                        }
                      `}
                      >
                        {stageOpportunities.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-32 text-center">
                            <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center mb-2 border border-slate-200 border-dashed shadow-sm">
                              <Plus className="h-4 w-4 text-slate-400" />
                            </div>
                            <p className="text-slate-500 text-xs font-medium">
                              Sin oportunidades
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={onCreateOpportunity}
                              className="mt-1 h-7 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 text-[10px] font-bold px-3 rounded-md"
                            >
                              CREAR NUEVA
                            </Button>
                          </div>
                        ) : (
                          <>
                            {stageOpportunities.map((opportunity, index) => (
                              <OpportunityCard
                                key={opportunity.id}
                                opportunity={opportunity}
                                index={index}
                                stages={stages}
                                loadingId={loadingId}
                                currencySymbol={globalCurrencySymbol}
                                wonStageId={wonStageId}
                                lostStageId={lostStageId}
                                onClick={onOpportunityClick}
                                onQuickAction={handleQuickAction}
                              />
                            ))}
                            {provided.placeholder}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      ) : (
        <OpportunityTable
          opportunities={opportunities}
          stages={stages}
          slug={slug}
        />
      )}

      <Dialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-red-700 text-lg">
              <AlertCircle className="h-6 w-6" />
              Movimiento Bloqueado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {validationAlert?.reason && (
              <div className="bg-gradient-to-br from-red-100 to-red-200 border-2 border-red-400 rounded-xl p-5">
                <p className="text-base font-bold text-red-900 mb-3">
                  Razón del bloqueo:
                </p>
                <p className="text-sm text-red-800 whitespace-pre-wrap leading-relaxed">
                  {validationAlert.reason}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAlertOpen(false)}
              className="w-full border-red-400 text-red-800 hover:bg-red-50 hover:text-red-900 text-sm h-10"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
